import {useCallback, useEffect, useRef, useState} from "react";
import NotificationPopUp from "util/NotificationPopUp";
import {
    InstallJobStatusDTO,
    InstallState,
} from "../../../../serviceapi/jobRunnerApi";
import {useJobRunnerApis} from "../hooks/useJobRunnerApis";

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_MS = 30 * 60 * 1000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const usePythonEnvironments = () => {
    const {pythonManagementApi} = useJobRunnerApis();

    const [environments, setEnvironments] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isInstalling, setIsInstalling] = useState<boolean>(false);
    const [installStatus, setInstallStatus] =
        useState<InstallJobStatusDTO | null>(null);

    const cancelledRef = useRef<boolean>(false);
    useEffect(() => {
        cancelledRef.current = false;
        return () => {
            cancelledRef.current = true;
        };
    }, []);

    const fetchEnvironments = useCallback(async () => {
        try {
            setIsLoading(true);
            const response =
                await pythonManagementApi.pythonManagementListGet();
            setEnvironments(response.data ?? []);
        } catch (error) {
            NotificationPopUp.handleGenericError(
                error,
                "Failed to fetch Python environments."
            );
        } finally {
            setIsLoading(false);
        }
    }, [pythonManagementApi]);

    const resetInstallStatus = useCallback(() => {
        setInstallStatus(null);
    }, []);

    const installEnvironment = useCallback(
        async (file: File, id?: string) => {
            try {
                setIsInstalling(true);
                setInstallStatus(null);

                const installResponse =
                    await pythonManagementApi.pythonManagementInstallPost(
                        file,
                        id?.trim() || undefined
                    );
                const jobId = installResponse.data as string;

                const startedAt = Date.now();
                let status: InstallJobStatusDTO | null = null;

                while (!cancelledRef.current) {
                    const statusResponse =
                        await pythonManagementApi.pythonManagementInstallJobIdGet(
                            jobId
                        );
                    status = statusResponse.data;
                    setInstallStatus(status);

                    if (
                        status?.state === InstallState.Succeeded ||
                        status?.state === InstallState.Failed
                    ) {
                        break;
                    }

                    if (Date.now() - startedAt > MAX_POLL_MS) {
                        NotificationPopUp.handleGenericError(
                            "Timed out waiting for the installation to finish.",
                            "Python environment install timed out."
                        );
                        break;
                    }

                    await wait(POLL_INTERVAL_MS);
                }

                if (cancelledRef.current) {
                    return;
                }

                if (status?.state === InstallState.Succeeded) {
                    NotificationPopUp.show(
                        "Python environment installed.",
                        "success"
                    );
                    await fetchEnvironments();
                } else if (status?.state === InstallState.Failed) {
                    NotificationPopUp.handleGenericError(
                        status.error ?? "The installation failed.",
                        "Python environment install failed."
                    );
                }
            } catch (error) {
                NotificationPopUp.handleGenericError(
                    error,
                    "Failed to install Python environment."
                );
            } finally {
                if (!cancelledRef.current) {
                    setIsInstalling(false);
                }
            }
        },
        [pythonManagementApi, fetchEnvironments]
    );

    const deleteEnvironment = useCallback(
        async (id: string) => {
            try {
                await pythonManagementApi.pythonManagementIdDelete(id);
                NotificationPopUp.show(`Deleted environment ${id}.`, "success");
                await fetchEnvironments();
            } catch (error) {
                NotificationPopUp.handleGenericError(
                    error,
                    "Failed to delete Python environment."
                );
            }
        },
        [pythonManagementApi, fetchEnvironments]
    );

    return {
        environments,
        isLoading,
        isInstalling,
        installStatus,
        fetchEnvironments,
        installEnvironment,
        deleteEnvironment,
        resetInstallStatus,
    };
};
